'use client';

import React from 'react';
import { BookHeart, Shield, Building2, AlertCircle } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';

const TIER_ICONS = [
  <BookHeart size={20} key="bookheart" />,
  <Shield size={20} key="shield" />,
  <Building2 size={20} key="building2" />,
];

const TIER_COLORS = ['#64748b', '#0891b2', '#d97706'];

const TIER_KEYS = [
  {
    titleKey: 'landing_verification.tier1_title',
    itemKeys: [
      'landing_verification.tier1_item1',
      'landing_verification.tier1_item2',
      'landing_verification.tier1_item3',
      'landing_verification.tier1_item4',
    ],
    badgeKey: 'landing_verification.tier1_badge',
  },
  {
    titleKey: 'landing_verification.tier2_title',
    itemKeys: [
      'landing_verification.tier2_item1',
      'landing_verification.tier2_item2',
      'landing_verification.tier2_item3',
      'landing_verification.tier2_item4',
    ],
    badgeKey: 'landing_verification.tier2_badge',
  },
  {
    titleKey: 'landing_verification.tier3_title',
    itemKeys: [
      'landing_verification.tier3_item1',
      'landing_verification.tier3_item2',
      'landing_verification.tier3_item3',
      'landing_verification.tier3_item4',
    ],
    badgeKey: 'landing_verification.tier3_badge',
  },
];

export default function VerificationNeedsSection() {
  const { t } = useTranslation();
  return (
    <section
      id="verification"
      className="scroll-mt-24 py-20 sm:py-24"
      style={{ background: '#F8F9FC' }}
    >
      <div className="mx-auto max-w-screen-2xl px-6 lg:px-10 2xl:px-16">
        <div className="mb-12 text-center">
          <h2 className="mb-3 text-3xl font-bold tracking-tight text-[#002B7F] sm:text-4xl">
            {t('landing_verification.title' as any)}
          </h2>
          <p className="mx-auto max-w-2xl text-slate-600">
            {t('landing_verification.subtitle' as any)}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {TIER_KEYS.map((tier, idx) => {
            const color = TIER_COLORS[idx];
            return (
              <div
                key={tier.titleKey}
                className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
                style={{ borderTop: `3px solid ${color}` }}
              >
                <div
                  className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
                  style={{ background: `${color}15`, color }}
                >
                  {TIER_ICONS[idx]}
                </div>
                <h3 className="mb-2 text-lg font-bold text-[#002B7F]">{t(tier.titleKey as any)}</h3>
                <ul className="mb-4 space-y-2">
                  {tier.itemKeys.map((key) => (
                    <li key={key} className="flex items-start gap-2 text-sm text-slate-600">
                      <span
                        className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full"
                        style={{ background: color }}
                      />
                      {t(key as any)}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t(tier.badgeKey as any)}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0 text-amber-700" />
          <p className="text-sm leading-relaxed text-amber-950/90">
            {t('landing_verification.disclaimer' as any)}
          </p>
        </div>
      </div>
    </section>
  );
}
